import { useNavigate, useParams } from 'react-router-dom';

import {
  FullScreenLoading,
  AcademicEnrollmentType,
  TrackAssessmentResult,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { useGetTrackAssessmentResultQuery } from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';

import { ChecklistDataOverrides } from './assessMyTrack.types';
import { AssessMyTrackPage } from './AssessMyTrackPage';
import { useAgentContext } from './AssessMyTrackPageResults.agentContext';

const transformChecklistOverrides = (data: TrackAssessmentResult): ChecklistDataOverrides => ({
  profile_image_url: data.profile_image_url || '',
  academicMetrics: {
    high_school_gpa: data.gpa,
    post_secondary_gpa: data.gpa,
    sat_score: data.sat_score,
    act_score: data.act_score,
  },
  academicRigor: data.academic_rigor_id,
  academicMajors: data.academicMajors
    ? data.academicMajors.map((major: { id: number; name: string }) => ({
        id: 0,
        user_academics_id: 0,
        academic_majors_id: major.id,
        major_gpa: 0,
        created_at: '',
        updated_at: '',
      }))
    : [],
  academicsPostSecondaryCurrentEnrollment: data.school_name
    ? {
        id: 0,
        school: data.school_name,
      }
    : undefined,
  academicEnrollmentTypes: {
    id: 0,
    level: data.enrollment_type_level as AcademicEnrollmentType,
  },
  documentsResumes: data.resume_media_id
    ? [
        {
          id: data.resume_media_id,
          user_id: data.user_id,
          category_id: '',
          question_id: '',
          option_id: 'resume' as const,
          profile_option_answers_id: 0,
          title: 'Resume',
          description: '',
          visibility: 'private',
          media_id: data.resume_media_id,
          media_type: 'document' as const,
          media_url: '',
          created_at: data.created_at,
          updated_at: data.updated_at,
        },
      ]
    : [],
  documentsTranscripts: data.transcript_media_id
    ? [
        {
          id: data.transcript_media_id,
          user_id: data.user_id,
          category_id: '',
          question_id: '',
          option_id: 'transcript' as const,
          profile_option_answers_id: 0,
          title: 'Transcript',
          description: '',
          visibility: 'private',
          media_id: data.transcript_media_id,
          media_type: 'document' as const,
          media_url: '',
          created_at: data.created_at,
          updated_at: data.updated_at,
        },
      ]
    : [],
  career_user_response_set_id: data.career_user_response_set_id || '',
});

type AssessMyTrackPageResultsProps = Readonly<{
  isShared?: boolean;
}>;

export const AssessMyTrackPageResults = ({ isShared = false }: AssessMyTrackPageResultsProps) => {
  const { trackAssessmentResultUuid } = useParams<{ trackAssessmentResultUuid: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetTrackAssessmentResultQuery(
    trackAssessmentResultUuid || '',
  );

  useAgentContext(data);

  if (isLoading) {
    return <FullScreenLoading />;
  }

  if (error || !data) {
    navigate(APP_ROUTE.ASSESS_MY_TRACK);

    return;
  }

  const checklistOverrides: ChecklistDataOverrides = transformChecklistOverrides(data);

  return (
    <AssessMyTrackPage checklistOverrides={checklistOverrides} results={data} isShared={isShared} />
  );
};
